import { Mail, Phone, MapPin } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { COMPANY } from "@/data/content";
import { isAnalyticsEnabled } from "@/lib/analytics";

const LAST_UPDATED = "September 9, 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-3">{title}</h2>
      <div className="text-slate-400 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  const { config } = useSite();
  const val = (k, fallback) => (config && config[k]) || fallback;
  const email = val("email", "[ENTER ACTUAL EMAIL]");
  const phone = val("phone", "[ENTER ACTUAL PHONE NUMBER]");
  const address = val("address", "[ENTER ACTUAL ADDRESS]");
  const analyticsOn = isAnalyticsEnabled();
  const brandName = config?.brand || COMPANY.name;
  const domain = config?.domain || COMPANY.domain;

  useSeo({
    title: `Privacy Policy | ${brandName}`,
    description: `How ${brandName} collects, uses, stores and protects information submitted through this website.`,
    canonical: config?.privacy_policy_url || `https://${domain}/privacy-policy`,
  });

  return (
    <div>
      <PageHeader
        label="Legal"
        title="Privacy Policy"
        sub={`Last updated: ${LAST_UPDATED}. This page explains what information ${brandName} collects through this website, how it is used, and the choices you have.`}
      />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Section id="introduction" title="1. Introduction">
          <p>
            This Privacy Policy applies to the {brandName} website ({domain}) (the "Site"), operated by {brandName}
            ("we", "us", "our"). It describes what information we collect when you visit or use this Site, why we collect it,
            how we use and protect it, and the choices available to you.
          </p>
          <p>
            By using this Site, you agree to the collection and use of information as described here. If you do not agree,
            please do not submit information to us through this Site.
          </p>
        </Section>

        <Section id="information-we-collect" title="2. Information We Collect">
          <p>We only collect information in the following ways:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Information you voluntarily submit through forms on this Site (see Section 3 below).</li>
            <li>
              Standard technical information that any website server automatically records for security and operational
              purposes as part of normal web hosting (for example, IP address, browser type, and pages requested), used
              only for site operation, security and troubleshooting — not for profiling or advertising.
            </li>
            <li>
              A single essential authentication cookie, set only when an authorised administrator logs in (see Section 7).
            </li>
          </ul>
        </Section>

        <Section id="forms" title="3. Information Submitted Through Forms">
          <p>Through the "Contact Us" and "Get a Quote" forms on this Site, we may ask for:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Name and mobile number (required)</li>
            <li>Email address, company name and city (optional)</li>
            <li>Your product/solution requirement, approximate system capacity, and any message or project details you choose to share</li>
          </ul>
          <p>We do not require you to create an account or provide this information to browse the Site.</p>
        </Section>

        <Section id="how-we-use" title="4. How We Use Information">
          <p>Information submitted through our forms is used only to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Respond to your enquiry and provide the product, solution or quote information you requested;</li>
            <li>Contact you by phone, email or WhatsApp regarding your enquiry;</li>
            <li>Maintain an internal record of enquiries for our sales and support team; and</li>
            <li>Improve the accuracy and usefulness of our products, solutions and customer service.</li>
          </ul>
          <p>We do not use the information you submit for automated decision-making or profiling.</p>
        </Section>

        <Section id="lead-information" title="5. Lead / Contact Information">
          <p>
            Enquiries submitted through this Site (referred to internally as "leads") are stored in our database and are
            accessible only to authorised {brandName} personnel through a password-protected admin area. We do not sell,
            rent or trade your enquiry information to third parties.
          </p>
        </Section>

        <Section id="communications" title="6. Communications and Email">
          <p>
            When you submit an enquiry, our system may send an automated internal email notification to our team so we can
            respond promptly; this does not send you a marketing email. Our team may then contact you directly using the
            phone number or email address you provided, solely regarding your enquiry. We do not send unsolicited marketing
            emails to addresses collected through this Site.
          </p>
        </Section>

        <Section id="cookies" title="7. Cookies and Similar Technologies">
          <p>
            This Site does not use cookies for general visitors browsing the public pages, and does not use any tracking or
            advertising cookies at this time.
          </p>
          <p>
            A single essential cookie is used exclusively to keep an authorised administrator securely signed in to the
            admin area after they log in. This cookie is set to be inaccessible to page scripts, is transmitted only over a
            secure (HTTPS) connection, and is not used to track visitors of the public website.
          </p>
        </Section>

        <Section id="analytics" title="8. Analytics / Tracking">
          {analyticsOn ? (
            <p>
              This Site uses Google Analytics to understand how visitors use the Site, so we can improve it. Google
              Analytics may use cookies and similar technologies to collect anonymised or pseudonymised usage statistics
              (such as pages viewed). You can control this through your browser's cookie and privacy settings.
            </p>
          ) : (
            <p>
              This Site currently does not have website analytics or visitor-tracking tools active. Our codebase includes
              optional support for Google Analytics 4 that can be switched on in the future; if and when it is enabled,
              this policy will be updated to describe exactly what is collected before it goes live.
            </p>
          )}
        </Section>

        <Section id="third-party" title="9. Third-Party Services">
          <p>We use a small number of third-party service providers to operate this Site. They process data only to provide these services to us, not for their own marketing purposes:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Hosting:</strong> our website and application are hosted on Hostinger's infrastructure.</li>
            <li><strong>Database:</strong> enquiry/lead data and site content are stored in MongoDB Atlas, a managed cloud database service, secured with access controls and encrypted connections.</li>
            <li><strong>Email delivery:</strong> when configured, outgoing notification emails are sent through a standard SMTP email provider.</li>
            <li><strong>Analytics (optional, currently inactive):</strong> Google Analytics, only if and when enabled per Section 8.</li>
          </ul>
        </Section>

        <Section id="meta" title="10. Meta Platforms / Facebook / Instagram Data">
          <p>
            This Site may display links to our official Facebook and/or Instagram pages. Visiting those pages is subject to
            Meta's own privacy policy and terms, not this one.
          </p>
          <p>
            <strong>Current status:</strong> this Site does not currently integrate with the Facebook, Instagram or Meta
            APIs (for example, Meta Login, Messenger, WhatsApp Business API, or Lead Ads), and we do not currently collect
            or receive any data about you from Meta Platforms, Inc. through this Site.
          </p>
          <p>
            <strong>If this changes:</strong> should we integrate any Facebook, Instagram or other Meta Platforms
            functionality into this Site or our business processes in the future, any information we receive through such
            integrations will be collected and used only for the purposes disclosed to you at that time, in accordance with
            this Privacy Policy (as updated) and Meta's applicable Platform Terms and Developer Policies.
          </p>
          <p>
            <strong>Data deletion callback:</strong> as required by Meta's Platform Terms for any app offering Facebook
            Login, we maintain a data-deletion request mechanism at{" "}
            <a href="/data-deletion" className="text-emerald-400 hover:underline">/data-deletion</a> and its
            supporting technical callback, ready for if/when a Facebook Login integration is added — see that page for
            how to request deletion of any data received this way.
          </p>
        </Section>

        <Section id="storage-security" title="11. How We Store and Protect Information">
          <p>
            Data submitted to this Site is transmitted over encrypted (HTTPS) connections and stored in a managed,
            access-controlled cloud database (MongoDB Atlas). Administrator accounts are protected with hashed passwords
            and a secure, time-limited login session. We take reasonable technical and organisational measures to protect
            information against unauthorised access, alteration or loss; however, no method of electronic storage or
            transmission is completely secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section id="retention" title="12. Data Retention">
          <p>
            We retain enquiry/lead information for as long as reasonably necessary to respond to your enquiry, maintain
            business records, and comply with applicable legal or accounting requirements. You may request deletion of
            your information at any time — see Section 15.
          </p>
        </Section>

        <Section id="sharing" title="13. Data Sharing / Disclosure">
          <p>We do not sell your personal information. We may share information only:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>With the service providers named in Section 9, solely to operate this Site and respond to your enquiry;</li>
            <li>If required to do so by law, regulation, or a valid legal request; or</li>
            <li>To protect the rights, property or safety of {brandName}, our users, or the public.</li>
          </ul>
        </Section>

        <Section id="rights" title="14. User Rights and Choices">
          <p>
            You may ask us, at any time, to tell you what information we hold about you, to correct inaccurate
            information, to stop contacting you, or to delete your information (subject to Section 12). You may also
            choose not to submit information to us, though this may limit our ability to respond to your enquiry.
          </p>
        </Section>

        <Section id="deletion" title="15. Data Deletion / Request Process">
          <p>
            To request access to, correction of, or deletion of information you have submitted to us, please contact us
            using the details in Section 18 (email is the fastest way to reach us). We will action reasonable requests
            within a reasonable timeframe, subject to any records we are legally required to keep.
          </p>
          <p>
            A dedicated <a href="/data-deletion" className="text-emerald-400 hover:underline">Data Deletion</a> page and
            an automated technical callback (required by Meta's Platform Terms for apps offering Facebook Login) are
            also available, for requests made through a Facebook/Meta integration specifically.
          </p>
        </Section>

        <Section id="children" title="16. Children's Privacy">
          <p>
            This Site is intended for businesses and adult individuals seeking solar, energy storage and power solutions.
            It is not directed at children, and we do not knowingly collect personal information from children. If you
            believe a child has provided us with personal information, please contact us and we will remove it.
          </p>
        </Section>

        <Section id="changes" title="17. Changes to This Privacy Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal reasons.
            Any changes will be posted on this page with a revised "Last updated" date. We encourage you to review this
            page periodically.
          </p>
          <p className="text-xs text-slate-500">
            This policy is written in plain language to describe our current practices and is not a substitute for legal
            advice. We aim to handle information responsibly and in line with applicable Indian data protection law
            (including the Digital Personal Data Protection Act, 2023, where it applies to us), but this page is not a
            certification or guarantee of compliance with any specific law, regulation, or platform's requirements.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            See also our <a href="/terms" className="text-emerald-400 hover:underline">Terms & Conditions</a>.
          </p>
        </Section>

        <Section id="contact" title="18. Contact Information">
          <p>If you have any questions about this Privacy Policy or wish to exercise any of the rights above, please contact us:</p>
          <div className="card-surface p-6 mt-2 space-y-3 text-sm not-italic">
            <div className="text-white font-semibold">{brandName}</div>
            <div className="flex items-start gap-3"><MapPin className="text-emerald-400 shrink-0" size={18} /><span>{address}</span></div>
            <div className="flex items-start gap-3"><Phone className="text-emerald-400 shrink-0" size={18} /><span>{phone}</span></div>
            <div className="flex items-start gap-3"><Mail className="text-emerald-400 shrink-0" size={18} /><span>{email}</span></div>
          </div>
        </Section>
      </section>
    </div>
  );
}
